import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import API_BASE from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import BlogLanding from './home/BlogLanding';
import { useBlogLanding } from './home/useBlogLanding';
import type { BlogPostDetail, BlogPostListItem, BlogRelated } from './home/blogTypes';

const Blog: React.FC = () => {
  const { slug } = useParams<{ slug?: string }>();
  const { language, setLanguage } = useLanguage();
  const lang = language === 'en' ? 'en' : 'cs';
  const v = useBlogLanding(lang);
  const mode = slug ? 'article' : 'list';

  const [posts, setPosts] = useState<BlogPostListItem[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(false);

  const [article, setArticle] = useState<BlogPostDetail | null>(null);
  const [related, setRelated] = useState<BlogRelated[]>([]);
  const [articleLoading, setArticleLoading] = useState(Boolean(slug));
  const [articleMissing, setArticleMissing] = useState(false);

  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = '';
    };
  }, []);

  useEffect(() => {
    if (mode !== 'list') return;
    let cancelled = false;
    setListLoading(true);
    setListError(false);
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/blog/posts`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed');
        if (!cancelled) setPosts(Array.isArray(data.posts) ? data.posts : []);
      } catch {
        if (!cancelled) {
          setListError(true);
          setPosts([]);
        }
      } finally {
        if (!cancelled) setListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, lang]);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setArticleLoading(true);
    setArticleMissing(false);
    setArticle(null);
    setRelated([]);
    window.scrollTo({ top: 0, behavior: 'auto' });
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/blog/posts/${encodeURIComponent(slug)}`);
        const data = await res.json();
        if (res.status === 404) {
          if (!cancelled) setArticleMissing(true);
          return;
        }
        if (!res.ok) throw new Error(data.error || 'Failed');
        if (!cancelled) {
          setArticle(data.post);
          setRelated(Array.isArray(data.related) ? data.related : []);
          document.title = `${data.post.title} · Ompli Blog`;
        }
      } catch {
        if (!cancelled) setArticleMissing(true);
      } finally {
        if (!cancelled) setArticleLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      document.title = 'Ompli';
    };
  }, [slug]);

  useEffect(() => {
    if (mode === 'list') {
      document.title = lang === 'cs' ? 'Blog · Ompli' : 'Blog · Ompli';
      return () => {
        document.title = 'Ompli';
      };
    }
  }, [mode, lang]);

  return (
    <BlogLanding
      v={v}
      lang={lang}
      onSetCz={() => setLanguage('cs')}
      onSetEn={() => setLanguage('en')}
      mode={mode}
      posts={posts}
      listLoading={listLoading}
      listError={listError}
      article={article}
      related={related}
      articleLoading={articleLoading}
      articleMissing={articleMissing}
    />
  );
};

export default Blog;
